import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../../components/SeoMeta';
import CmsImage from '../../components/CmsImage';
import Lightbox from '../../components/Lightbox';
import { fetchCollection, fetchLocalizedCollection } from '../../lib/cms';

export default function UsedPressesPageEn() {
  const [machines, setMachines] = useState([]);
  const [pageTitle, setPageTitle] = useState('Used Machines');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    fetchLocalizedCollection('brugte_maskiner', 'en').then((items) => {
      if (items && items.length) {
        setMachines(items.sort((a, b) => (a.sortering || 0) - (b.sortering || 0)));
      }
    });

    fetchLocalizedCollection('ydelser_produkter', 'en').then((items) => {
      const match = items.find(
        (i) => i.currentSlug === 'presser-2' || i.currentSlug === 'brugte-maskiner' || i.id === '285aefc2-89ea-432b-95c7-33bde3684b54'
      );
      if (match?.titel) {
        setPageTitle(match.titel);
      }
    });
  }, []);

  const galleryImages = machines.map((m) => {
    const url = Array.isArray(m.billede) ? m.billede[0] : m.billede;
    return { url, title: m.titel };
  });

  return (
    <>
      <SeoMeta
        title={`${pageTitle} – Inspected Refurbished Machinery with Warranty`}
        description={`${pageTitle} – Operational reliability with care. Thoroughly inspected eccentric presses and coil decoilers with full documentation and warranty.`}
      />

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/en" className="hover:text-[#006368] transition">
            Home
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <Link to="/en/presses" className="hover:text-[#006368] transition">
            Presses
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">{pageTitle}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
              Quality Inspected Machinery
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {pageTitle}
            </h1>
            <p className="mt-2 text-xl text-[#0d8086] font-semibold">
              operational reliability with care
            </p>
            <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed">
              At <strong>PMB</strong>, we recognize the enduring value of precision-built engineering. With more than 60 years of machine manufacturing experience, we revitalize used presses – ensuring they generate reliable productivity in your workshop for decades to come.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#machinelist"
                className="px-6 py-3 rounded bg-[#006368] hover:bg-[#004d51] text-white font-semibold text-sm tracking-wide transition shadow-sm flex items-center gap-2"
              >
                <span>Browse current machines</span>
                <i className="fa-solid fa-arrow-down text-xs" />
              </a>
              <Link
                to="/en/contact"
                className="px-6 py-3 rounded bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition whitespace-nowrap"
              >
                Searching for a specific machine?
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value Pillars */}
      <section className="py-16 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl font-bold text-stone-900 mb-8">
            When choosing a used press from PMB, you receive a machine that is:
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-shield-check text-lg" />
              </div>
              <h3 className="font-bold text-stone-900 text-base">Inspected & Tested</h3>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed">
                Refurbished and rigorously tested in Borup to the exact same standards as new machinery.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-file-lines text-lg" />
              </div>
              <h3 className="font-bold text-stone-900 text-base">Full Documentation</h3>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed">
                Supplied with operating instructions, electrical schematics, and an understandable service schedule.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-sliders text-lg" />
              </div>
              <h3 className="font-bold text-stone-900 text-base">Custom Adaptation</h3>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed">
                Option for factory retrofit, upgraded safety light curtains, or new decoilers tailored to your tooling.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-circle-check text-lg" />
              </div>
              <h3 className="font-bold text-stone-900 text-base">Warranty & Parts</h3>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed">
                Up to 12 months warranty on refurbished units with guaranteed long-term spare parts availability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Machine inventory list */}
      <section id="machinelist" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#006368] block mb-1">
                Current Inventory
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                Available Used Presses & Equipment
              </h2>
            </div>
            <span className="text-sm text-stone-500">
              {machines.length} machines ready for evaluation
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {machines.map((m, idx) => {
              const mainImg = Array.isArray(m.billede) ? m.billede[0] : m.billede;
              return (
                <div
                  key={m.id || idx}
                  className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm hover:shadow-lg transition duration-200 flex flex-col justify-between"
                >
                  <div>
                    {/* Machine Photo */}
                    <div
                      onClick={() => {
                        setLightboxIndex(idx);
                        setLightboxOpen(true);
                      }}
                      className="h-64 overflow-hidden bg-stone-100 cursor-pointer relative group"
                    >
                      <CmsImage
                        src={mainImg}
                        alt={m.titel}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                        width={500}
                      />
                      <div className="absolute top-3 right-3 px-2.5 py-1 rounded bg-stone-900/80 text-white text-[11px] font-bold">
                        {m.aarstal || 'Inspected'}
                      </div>
                      <div className="absolute inset-0 bg-[#006368]/20 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white">
                        <span className="p-2.5 rounded-full bg-stone-900/80">
                          <i className="fa-solid fa-magnifying-glass" />
                        </span>
                      </div>
                    </div>

                    {/* Specs & Description */}
                    <div className="p-6">
                      <div className="text-xs font-bold uppercase tracking-wider text-[#006368] mb-1">
                        {m.maskintype || 'Press'}
                      </div>
                      <h3 className="text-xl font-bold text-stone-900 mb-2">{m.titel}</h3>
                      {m.beskrivelse && (
                        <p className="text-xs text-stone-600 leading-relaxed mb-4">{m.beskrivelse}</p>
                      )}

                      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs border-t border-stone-100 pt-4">
                        <div>
                          <dt className="text-stone-500 font-medium">Manufacturer:</dt>
                          <dd className="font-semibold text-stone-900 truncate">
                            {m.producent || '-'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-stone-500 font-medium">Model:</dt>
                          <dd className="font-semibold text-stone-900 truncate">
                            {m.model || '-'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-stone-500 font-medium">Capacity:</dt>
                          <dd className="font-semibold text-[#006368]">
                            {m.pressekraft || '-'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-stone-500 font-medium">Stroke length:</dt>
                          <dd className="font-semibold text-stone-900">
                            {m.slaglaengde || '-'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-stone-500 font-medium">Speed:</dt>
                          <dd className="font-semibold text-stone-900">
                            {m.hastighed || '-'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-stone-500 font-medium">Weight:</dt>
                          <dd className="font-semibold text-stone-900">
                            {m.vaegt || '-'}
                          </dd>
                        </div>
                        {m.fabrikationsnr && (
                          <div className="col-span-2 pt-1 border-t border-stone-50">
                            <span className="text-stone-400">Serial no.:</span>{' '}
                            <span className="font-mono text-stone-700">{m.fabrikationsnr}</span>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>

                  <div className="p-6 pt-0 border-t border-stone-100 flex items-center justify-between">
                    <Link
                      to={`/en/contact?presser=${encodeURIComponent(m.titel)}`}
                      className="w-full text-center py-2.5 px-4 rounded bg-[#006368] hover:bg-[#004d51] text-white text-xs font-bold tracking-wide transition shadow-2xs"
                    >
                      Inquire about this machine
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Inquiry Box */}
      <section className="py-16 bg-stone-50 border-t border-stone-200">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-4">
          <h3 className="text-2xl font-bold text-stone-900">
            Looking for a specific model or capacity?
          </h3>
          <p className="text-stone-600 text-sm max-w-xl mx-auto">
            Our machine inventory updates continuously. If you are searching for a particular eccentric press, hydraulic unit, or coil line, contact our sales engineers directly.
          </p>
          <div className="pt-2">
            <Link
              to="/en/contact"
              className="px-6 py-3 rounded bg-[#006368] hover:bg-[#004d51] text-white font-semibold text-sm transition inline-block"
            >
              Contact our sales department
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={galleryImages}
        currentIndex={lightboxIndex}
        setCurrentIndex={setLightboxIndex}
      />
    </>
  );
}
